import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Smartphone, Globe, Code, Zap, Shield, TrendingUp, CheckCircle2, ArrowRight } from 'lucide-react';
import { UploadForm } from './components/UploadForm';
import { AdminLogin } from './components/AdminLogin';
import { AdminPage } from './components/AdminPage';
import { FloatingChatIcons } from './components/FloatingChatIcons';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { SEOContent } from './components/SEOContent';
import { BlogSection } from './components/BlogSection';
import { VideoWatchPage } from './components/VideoWatchPage';
import VideoSection from './components/VideoSection';
import { Logo } from './components/Logo';
import { ParticleBackground } from './components/ParticleBackground';
import { AnimatedLoading } from './components/AnimatedLoading';
import { BlogPage } from './pages/BlogPage';
import { useTranslation } from './hooks/useTranslation';
import { useSEO } from './hooks/useSEO';
import { useScrollAnimation } from './hooks/useScrollAnimation';
import { supabase } from './lib/supabase';
import './styles/animations.css';

function MainContent() {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  const { t, language, isLoading: langLoading } = useTranslation();
  
  // SEO optimization for all languages
  useSEO(language);

  // Animation hooks
  const [heroRef] = useScrollAnimation();
  const [featuresRef] = useScrollAnimation();
  const [benefitsRef] = useScrollAnimation();
  const [processRef] = useScrollAnimation();
  const [ctaRef] = useScrollAnimation();

  useEffect(() => {
    checkAuth();
    checkRoute();

    const { data: authListener } = supabase.auth.onAuthStateChange();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Force re-render when language changes
  useEffect(() => {
    console.log('Language changed to:', language); // Debug log
  }, [language]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsAuthenticated(!!session);
    setLoading(false);
  };

  const checkRoute = () => {
    const path = window.location.pathname;
    if (path === '/admin') {
      setIsAdmin(true);
    }
  };

  if (loading || langLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AnimatedLoading size="lg" text="Đang tải..." />
          <div className="mt-8">
            <Logo size="lg" className="text-white animate-pulse-slow" />
          </div>
        </div>
      </div>
    );
  }

  if (isAdmin) {
    if (!isAuthenticated) {
      return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
    }
    return <AdminPage />;
  }

  // Show video watch page if video is selected
  if (currentVideoId) {
    return <VideoWatchPage videoId={currentVideoId} onBack={() => setCurrentVideoId(null)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Particle Background */}
      <ParticleBackground 
        particleCount={30} 
        colors={['#10b981', '#06b6d4', '#8b5cf6', '#f59e0b']}
        speed={0.3}
        size={{ min: 1, max: 2 }}
      />
      
      {/* Hero Section */}
      <header 
        ref={heroRef}
        className="container mx-auto px-6 pt-20 pb-32 relative z-10"
      >
        <nav className="flex justify-between items-center mb-20">
          <Logo size="lg" className="text-white" />
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <button
              onClick={() => setShowUploadForm(true)}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-all duration-300 hover:scale-105 hover-glow animate-pulse-slow focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
              aria-label="Contact DeployPro for app deployment services"
            >
              {t('hero.contact_now')}
            </button>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {t('hero.title')}
          </h1>
          <p className="text-xl text-slate-300 mb-10 leading-relaxed">
            {t('hero.subtitle')}
          </p>
          <button
            onClick={() => setShowUploadForm(true)}
            className="group px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white text-lg rounded-lg font-semibold transition-all duration-300 hover:scale-105 inline-flex items-center space-x-2 shadow-lg shadow-emerald-500/30 hover-glow focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
            aria-label="Start your app deployment journey with DeployPro"
          >
            <span>{t('hero.cta')}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Video Section */}
      <VideoSection 
        onContactClick={() => setShowUploadForm(true)} 
        onWatchVideo={(videoId) => setCurrentVideoId(videoId)}
      />

      {/* Blog Section */}
      <BlogSection />

      {/* Features Section */}
      <section 
        ref={featuresRef}
        className="bg-white py-20 relative z-10"
      >
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 animate-scale-in">
              {t('features.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto animate-fade-in-up stagger-1">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="group p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 hover-lift">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform animate-float">
                <Smartphone className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t('features.android_ios')}</h3>
              <p className="text-slate-600 leading-relaxed">
                {t('features.android_ios_desc')}
              </p>
            </div>

            <div className="group p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 hover-lift">
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform animate-float">
                <Globe className="w-7 h-7 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t('features.website')}</h3>
              <p className="text-slate-600 leading-relaxed">
                {t('features.website_desc')}
              </p>
            </div>

            <div className="group p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 hover-lift">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform animate-float">
                <Code className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t('features.source_code')}</h3>
              <p className="text-slate-600 leading-relaxed">
                {t('features.source_code_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

              {/* Benefits Section */}
              <section 
                ref={benefitsRef}
                className="py-20 bg-slate-900 relative z-10"
              >
                <div className="container mx-auto px-6">
                  <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                      <h2 className="text-4xl font-bold text-white mb-4 animate-glow">
                        {t('benefits.title')}
                      </h2>
                      <p className="text-lg text-slate-300 animate-fade-in-up stagger-1">
                        {t('benefits.subtitle')}
                      </p>
                    </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-yellow-400/50 transition-all duration-300 hover-lift hover-glow animate-fade-in-up">
                <div className="w-12 h-12 bg-yellow-400/20 rounded-lg flex items-center justify-center mb-4 animate-pulse-slow">
                  <Zap className="w-6 h-6 text-yellow-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{t('benefits.fast_deployment')}</h3>
                <p className="text-slate-400">
                  {t('benefits.fast_deployment_desc')}
                </p>
              </div>

              <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-emerald-400/50 transition-all duration-300 hover-lift hover-glow animate-fade-in-up stagger-1">
                <div className="w-12 h-12 bg-emerald-400/20 rounded-lg flex items-center justify-center mb-4 animate-pulse-slow">
                  <Shield className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{t('benefits.high_security')}</h3>
                <p className="text-slate-400">
                  {t('benefits.high_security_desc')}
                </p>
              </div>

              <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-cyan-400/50 transition-all duration-300 hover-lift hover-glow animate-fade-in-up stagger-2">
                <div className="w-12 h-12 bg-cyan-400/20 rounded-lg flex items-center justify-center mb-4 animate-pulse-slow">
                  <TrendingUp className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{t('benefits.cost_optimized')}</h3>
                <p className="text-slate-400">
                  {t('benefits.cost_optimized_desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        ref={processRef}
        className="py-20 bg-white relative z-10"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                      <h2 className="text-4xl font-bold text-slate-900 mb-4 animate-scale-in">
                        {t('process.title')}
                      </h2>
                      <p className="text-lg text-slate-600 animate-fade-in-up stagger-1">
                        {t('process.subtitle')}
                      </p>
                    </div>

            <div className="space-y-6">
              {[
                { step: '01', title: t('process.step1'), desc: t('process.step1_desc') },
                { step: '02', title: t('process.step2'), desc: t('process.step2_desc') },
                { step: '03', title: t('process.step3'), desc: t('process.step3_desc') },
                { step: '04', title: t('process.step4'), desc: t('process.step4_desc') }
              ].map((item, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-start space-x-6 p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all duration-300 hover-lift animate-fade-in-up ${
                    idx === 0 ? '' : `stagger-${idx}`
                  }`}
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-emerald-500 text-white rounded-xl flex items-center justify-center font-bold text-xl animate-bounce-in hover-scale">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 animate-pulse-slow" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        ref={ctaRef}
        className="py-24 bg-gradient-to-r from-emerald-600 to-cyan-600 animate-gradient relative z-10"
      >
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-glow">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-emerald-50 mb-10 max-w-2xl mx-auto animate-fade-in-up stagger-1">
            {t('cta.subtitle')}
          </p>
          <button
            onClick={() => setShowUploadForm(true)}
            className="group px-10 py-4 bg-white hover:bg-slate-100 text-emerald-700 text-lg rounded-lg font-bold transition-all duration-300 hover:scale-105 inline-flex items-center space-x-2 shadow-xl hover-glow animate-bounce-in stagger-2 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-emerald-600"
            aria-label="Get started with professional app deployment services"
          >
            <span>{t('cta.contact_now')}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </button>
        </div>
      </section>

              {/* SEO Content Section */}
              <SEOContent />

              {/* Footer */}
              <footer className="bg-slate-900 py-12 border-t border-slate-800">
                <div className="container mx-auto px-6">
                  <div className="flex flex-col md:flex-row justify-between items-center">
                    <div className="flex items-center space-x-2 mb-4 md:mb-0">
                      <Code className="w-6 h-6 text-emerald-400" />
                      <span className="text-xl font-bold text-white">DeployPro</span>
                    </div>
                    <p className="text-slate-400">{t('footer.copyright')}</p>
                  </div>
                </div>
              </footer>

              {showUploadForm && <UploadForm onClose={() => setShowUploadForm(false)} />}

              {/* Floating Chat Icons */}
              <FloatingChatIcons />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainContent />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
      </Routes>
    </Router>
  );
}

export default App;